/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { actionCreators as roomActions } from "../../state/ducks/room";
import janusApi from "../../janus-api";
import {
  selectors as callSelectors,
  actionCreators as callActions,
} from "../../state/ducks/call";
import webSocketService from "../../utils/websocket";

import OutgoingCall from "./OutgoingCall";
import IncomingCall from "./IncomingCall";
import ActiveCall from "./ActiveCall";
import { apiUserPage, apiUpdateUser } from "../../api/member";
import { apiUpdateCallLog } from "../../api/CallLog";
import { useToastContext } from "../../components/Toast";

/**
 * 通话集成组件
 * 根据Redux状态管理整个通话流程，协调各个通话界面的显示
 */
const CallIntegration = () => {
  const dispatch = useDispatch();
  const toast = useToastContext();
  const currentCall = useSelector(callSelectors.getCurrentCall);
  const hasIncomingCall = useSelector(callSelectors.hasIncomingCall);
  const isOutgoingCall = useSelector(callSelectors.isOutgoingCall);
  const isInCall = useSelector(callSelectors.isInCall);
  const userInfo = useSelector((state) => state.user.userInfo);
  const { settings, error } = useSelector((state) => state.room);

  const [localAudioStream, setLocalAudioStream] = useState(null);
  const [remoteAudioStream, setRemoteAudioStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);

  const [isTest, setIsTest] = useState(true);

  const audioContextRef = useRef(null);
  const localStreamRef = useRef(null);

  const fetchContacts = async () => {
    try {
      const response = await apiUserPage({ pageNo: 1, pageSize: 100000 });
      //console.log(response);
      const userList = response.data?.list || [];
      // 将 API 返回的用户数据转换为联系人格式
      const contactsData = userList.map((user) => ({
        id: user.id,
        name: user.nickname || user.username || "未知用户",
        status: user.curStatus,
        lastSeen: new Date().toISOString(),
      }));
      // 更新 Redux store
      dispatch(callActions.setContacts(contactsData));
    } catch (error) {
      console.error("获取联系人失败:", error);
    } finally {
    }
  };

  // 初始化音频上下文和本地音频流
  useEffect(() => {
    const initAudio = async () => {
      try {
        // 初始化Web Audio API
        console.log("初始化Web Audio API");
        if (!audioContextRef.current) {
          audioContextRef.current = new (
            window.AudioContext || window.webkitAudioContext
          )();
        }

        // 获取用户媒体（仅音频）
        if (!localStreamRef.current) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          });
          localStreamRef.current = stream;
          setLocalAudioStream(stream);
        }
      } catch (error) {
        console.error("Failed to initialize audio:", error);
      }
    };

    initAudio();

    // 清理函数
    return () => {
      console.log("清理函数被调用1");
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // 监听 WebSocket 消息
  useEffect(() => {
    const handleWebSocketMessage = (data) => {
      console.log(
        "CallIntegration 收到 WebSocket 消息:",
        JSON.parse(data.content),
      );

      const resData = JSON.parse(data.content);

      // 根据消息类型处理
      switch (data.type) {
        case "update_list":
          fetchContacts();
          break;
        case "call_invite":
          // 处理来电邀请
          // 数据格式: { type: 'call_invite', callId, callerId, callerName, roomId, timestamp }
          console.log("收到来电邀请111:", JSON.parse(data.content));
          if (resData.callId && resData.callerId && resData.callerName) {
            // 更新 Redux 状态，显示来电界面
            // console.log(
            //   "更新 Redux 状态，显示来电界面====================",
            //   resData,
            // );
            dispatch(
              callActions.receiveIncomingCall(
                resData.callId,
                resData.callerId,
                resData.callerName,
                resData.roomId,
                false,
              ),
            );
            // 向父级窗口发送消息，通知有新通话
            if (window.parent && window.parent.postMessage) {
              window.parent.postMessage("有新的通话", "*");
            }
          }
          break;

        case "call_accept":
          // 处理对方接受通话
          // 数据格式: { type: 'call_accept', callId, acceptorId, roomId }
          console.log("对方已接受通话:", resData);
          if (resData.callId && currentCall?.callId === resData.callId) {
            // 更新通话状态为进行中
            // 需要修改的地方
            // 加入房间
            //janusApi.enterRoom({ id: currentCall.roomId }, userInfo.nickname, "");
            // const newSettings = {
            //   ...settings,
            //   username: userInfo.nickname,
            //   roomId: currentCall.roomId,
            // };
            // dispatch(roomActions.saveSettings(newSettings));
            // dispatch(roomActions.login(userInfo.nickname, currentCall.roomId));

            dispatch(callActions.acceptCall(resData.callId));

            // setTimeout(() => {
            //   dispatch(callActions.acceptCall(resData.callId));
            // }, 1500);
          }
          break;

        case "call_reject":
          // 处理对方拒绝通话
          // 数据格式: { type: 'call_reject', callId, rejectorId, reason }
          console.log("对方已拒绝通话:", resData);
          janusApi.leaveRoom();
          if (resData.callId && currentCall?.callId === resData.callId) {
            dispatch(callActions.rejectCall(resData.callId));
          }
          // 弹出信息
          toast.error(resData.msgText, { duration: 1500 });
          apiUpdateUser({ curStatus: "online" });
          break;

        case "call_end":
          // 处理对方结束通话
          // 数据格式: { type: 'call_end', callId, enderId }
          console.log("对方已结束通话:", resData);
          janusApi.leaveRoom();
          console.log("resLeave=====2");
          if (resData.callId && currentCall?.callId === resData.callId) {
            dispatch(callActions.endCall(resData.callId));
          }
          apiUpdateUser({ curStatus: "online" });
          break;

        case "call_cancel":
          // 处理对方取消通话
          // 数据格式: { type: 'call_cancel', callId, cancellerId }
          console.log("对方已取消通话:", resData);
          if (resData.callId && currentCall?.callId === resData.callId) {
            dispatch(callActions.endCall(resData.callId));
          }
          apiUpdateUser({ curStatus: "online" });
          break;

        default:
          // 其他类型的消息，可以根据需要处理
          if (data.type && data.type.startsWith("call_")) {
            console.log("未处理的通话消息类型:", data.type, data);
          }
          break;
      }
    };

    // 添加消息监听器
    webSocketService.on("message", handleWebSocketMessage);

    // 连接 WebSocket（如果尚未连接）
    const connectWebSocket = async () => {
      const status = webSocketService.getStatus();
      if (!status.isConnected && !status.isConnecting) {
        try {
          await webSocketService.connect();
          console.log("CallIntegration: WebSocket 连接成功");
        } catch (error) {
          console.error("CallIntegration: WebSocket 连接失败:", error);
        }
      }
    };

    connectWebSocket();

    // 清理函数：移除监听器
    return () => {
      webSocketService.off("message", handleWebSocketMessage);
    };
  }, [dispatch, currentCall?.callId, userInfo]);

  // 监听通话状态变化
  // useEffect(() => {
  //   if (currentCall && currentCall.state === "active") {
  //     // 通话激活，开始模拟远程音频流
  //     //simulateRemoteAudioStream();
  //   } else {
  //     // 清理远程音频流
  //     // if (remoteAudioStream) {
  //     //   remoteAudioStream.getTracks().forEach((track) => track.stop());
  //     //   setRemoteAudioStream(null);
  //     // }
  //   }
  // }, [currentCall?.state]);

  // 发送 WebSocket 通话消息

  // 处理发起通话
  const handleInitiateCall = (remoteUserId, remoteUsername) => {
    // 生成通话ID
    // const callId = `call_${Date.now()}_${Math.random()
    //   .toString(36)
    //   .substr(2, 9)}`;
    // // 更新Redux状态
    // dispatch(callActions.initiateCall(remoteUserId, remoteUsername, callId));
    // // 实际应用中：调用Janus API发起通话
    // // callService.initiateCall(remoteUserId, remoteUsername, callId);
    // // 通过 WebSocket 发送通话邀请消息
    // sendCallMessage("call_invite", {
    //   callId,
    //   callerId: remoteUserId, // 这里应该是当前用户的ID，需要从Redux状态获取
    //   callerName: remoteUsername, // 这里应该是当前用户的名称
    //   remoteUserId: remoteUserId,
    //   remoteUsername: remoteUsername,
    // });
    // console.log(`Initiating call to ${remoteUsername} (${remoteUserId})`);
  };

  // 处理接受来电
  const handleAcceptCall = async (callId, roomId) => {
    console.log(`Accepting call ${callId} in room ${roomId}`);

    // 更新通话记录状态为接通
    const res5 = await apiUpdateCallLog({
      callId: currentCall.callId,
      callStatus: 1,
      //acceptorId: parseInt(currentCall.remoteUserId),
      msgType: "call_accept",
    });

    janusApi.acceptCall(callId, roomId);
  };

  // 处理拒绝来电
  const handleRejectCall = async (reason) => {
    if (currentCall) {
      console.log(`Rejecting call ${currentCall.callId}: ${reason}`);

      // 更新自己的状态
      const res2 = await apiUpdateUser({
        curStatus: "online",
        acceptorId: parseInt(currentCall.remoteUserId),
        callId: currentCall.callId,
        msgType: "call_reject",
        msgText: reason,
      });
      //console.log("leaveRoom res=====2", res2);
      // 删除房间
      const res3 = await janusApi.destroyedRoom({
        room: currentCall.roomId,
        permanent: true,
      });
      //console.log("leaveRoom res=====3", res3);

      // 更新通话记录状态为拒绝
      const res5 = await apiUpdateCallLog({
        callId: currentCall.callId,
        callStatus: reason === "无人接听" ? 0 : 2,
      });
      //console.log("leaveRoom res=====5", res5);
    }
  };

  // 处理取消呼出
  const handleCancelCall = async () => {
    if (currentCall) {
      //console.log(`Cancelling outgoing call ${currentCall.callId}`);
      // 需要修改的地方
      // 发起者先退出房间
      const res = await janusApi.leaveRoom();
      //console.log("leaveRoom res=====1", res);
      // 更新自己的状态
      const res2 = await apiUpdateUser({
        curStatus: "online",
        acceptorId: parseInt(currentCall.remoteUserId),
        callId: currentCall.callId,
        msgType: "call_cancel",
      });
      //console.log("leaveRoom res=====2", res2);
      // 删除房间
      const res3 = await janusApi.destroyedRoom({
        room: currentCall.roomId,
        permanent: true,
      });
      //console.log("leaveRoom res=====3", res3);
      // 更新通话记录状态
      const res5 = await apiUpdateCallLog({
        callId: currentCall.callId,
        callStatus: 3,
      });
      //console.log("leaveRoom res=====5", res5);
    }
  };

  // 处理结束通话
  const handleEndCall = async () => {
    if (currentCall) {
      console.log(`Ending active call ${currentCall.callId}`);

      // 更新通话记录状态为结束
      const res5 = await apiUpdateCallLog({
        callId: currentCall.callId,
        callStatus: 4,
        //acceptorId: parseInt(currentCall.remoteUserId),
        msgType: "call_end",
      });
      apiUpdateUser({
        curStatus: "online",
        acceptorId: parseInt(currentCall.remoteUserId),
        msgType: "call_end",
      });
      const resLeave = await janusApi.leaveRoom();
      console.log("resLeave=====1", resLeave);
      // 删除房间
      const res3 = await janusApi.destroyedRoom({
        room: currentCall.roomId,
        permanent: true,
      });
      console.log("Ending active call leaveRoom res=====3", res3);
    }
  };

  // 处理静音切换
  const handleToggleMute = (muted) => {
    setIsMuted(muted);

    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = !muted;
      });
    }

    console.log(`Microphone ${muted ? "muted" : "unmuted"}`);
  };

  // 处理扬声器切换
  const handleToggleSpeaker = (enabled) => {
    console.log(`Speaker ${enabled ? "enabled" : "disabled"}`);
  };

  // 处理通话保持
  const handleHold = (hold) => {
    console.log(`Call ${hold ? "held" : "resumed"}`);
    // 实际应用中：暂停/恢复音频流
  };

  // 如果没有进行中的通话，不渲染任何内容
  if (!hasIncomingCall && !isOutgoingCall && !isInCall) {
    return null;
  }

  // 渲染呼出界面
  if (isOutgoingCall && currentCall) {
    return (
      <OutgoingCall
        callId={currentCall.callId}
        remoteUserId={currentCall.remoteUserId}
        remoteUsername={currentCall.remoteUsername}
        onCancel={handleCancelCall}
      />
    );
  }

  // 渲染来电界面
  if (hasIncomingCall && currentCall) {
    return (
      <IncomingCall
        callId={currentCall.callId}
        callerId={currentCall.remoteUserId}
        callerName={currentCall.remoteUsername}
        roomId={currentCall.callId} // 实际应用中：从Janus获取roomId
        onAccept={handleAcceptCall}
        onReject={handleRejectCall}
      />
    );
  }

  // 渲染通话中界面 isInCall && currentCall
  if (isInCall && currentCall) {
    return (
      <ActiveCall
        callId={currentCall.callId}
        remoteUserId={parseInt(currentCall.remoteUserId)}
        remoteUsername={currentCall.remoteUsername}
        shouldAutoRecord={currentCall.isInitiator}
        localAudioStream={localAudioStream}
        remoteAudioStream={remoteAudioStream}
        isMuted={isMuted}
        onToggleMute={handleToggleMute}
        onEndCall={handleEndCall}
        onHold={handleHold}
      />
    );
  }

  return null;
};

export default CallIntegration;
